import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { getUser, getUserLoading } from '../../data/selectors/userAuthSelectors';
import { useNavigate } from 'react-router-dom';
import { getAllOrgs } from '../../services/knowAdmin';
import { getSsoConfig } from '../../services/sso';
import LoadingSpinner from '../loadingSpinner/LoadingSpinner';
import SsoConfigSection from '../ssoConfig/SsoConfigSection';
import formatDate from '../../utils/formatDate';
import styles from './SsoAdmin.module.css';

export default function SsoAdmin() {
  const navigate = useNavigate();
  const admin = useSelector(getUser);
  const adminLoading = useSelector(getUserLoading);
  const [allOrgs, setAllOrgs] = useState([]);
  const [orgsLoading, setOrgsLoading] = useState(true);
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [ssoStatuses, setSsoStatuses] = useState({});

  const ssoEnabledOrgs = allOrgs.filter((org) => org.sso_enabled);

  useEffect(() => {
    document.title = 'KNOW - SSO Admin';
  }, []);

  useEffect(() => {
    if (admin.role !== 'know-admin') navigate('/');
  }, [admin.role, navigate]);

  useEffect(() => {
    if (admin.role === 'know-admin') {
      setOrgsLoading(true);
      getAllOrgs()
        .then(({ data }) => {
          const enabled = data.filter((org) => org.sso_enabled);
          setAllOrgs(data);
          if (enabled.length > 0 && enabled[0]._id) {
            setSelectedOrgId(enabled[0]._id);
          }
          setOrgsLoading(false);
        })
        .catch(() => setOrgsLoading(false));
    } else {
      setOrgsLoading(false);
    }
  }, [admin]);

  useEffect(() => {
    if (!allOrgs.length) return;
    const fetchStatuses = async () => {
      const statuses = {};
      await Promise.all(
        allOrgs.map(async (org) => {
          try {
            const { data } = await getSsoConfig(org._id);
            if (data.configured) {
              statuses[org._id] = {
                protocol: data.protocol,
                status: data.status,
                lastTestedAt: data.lastTestedAt,
                authMode: data.authMode,
              };
            } else {
              statuses[org._id] = null;
            }
          } catch {
            statuses[org._id] = null;
          }
        })
      );
      setSsoStatuses(statuses);
    };
    fetchStatuses();
  }, [allOrgs]);

  if (adminLoading || orgsLoading) return <LoadingSpinner />;

  const orgOptions = ssoEnabledOrgs.map((org) => (
    <option key={org._id} value={org._id}>
      {org.org_name}
    </option>
  ));

  const overviewRows = ssoEnabledOrgs.map((org) => {
    const sso = ssoStatuses[org._id];
    return (
      <tr key={org._id}>
        <td>{org.org_name}</td>
        <td>{sso ? sso.protocol.toUpperCase() : '—'}</td>
        <td>
          {sso ? (
            <span
              className={styles.statusBadge}
              style={{
                backgroundColor:
                  sso.status === 'active'
                    ? '#2d6a4f'
                    : sso.status === 'pending'
                      ? '#e6a700'
                      : '#c62828',
              }}
            >
              {sso.status}
            </span>
          ) : (
            'None'
          )}
        </td>
        <td>{sso?.lastTestedAt ? formatDate(sso.lastTestedAt) : '—'}</td>
        <td>{sso ? sso.authMode : '—'}</td>
        <td>
          <button
            type="button"
            className={styles.smallBtn}
            onClick={() => setSelectedOrgId(org._id)}
          >
            Select
          </button>
        </td>
      </tr>
    );
  });

  return (
    <div className={styles.adminContainer}>
      <h1>SSO Administration</h1>
      <button type="button" className={styles.backBtn} onClick={() => navigate('/know-admin')}>
        &larr; Back to Know Admin
      </button>

      <div className={styles.orgSelector}>
        <label htmlFor="orgSelect">Select Organization:</label>
        <select
          id="orgSelect"
          value={selectedOrgId || ''}
          onChange={(e) => setSelectedOrgId(Number(e.target.value))}
        >
          {orgOptions}
        </select>
      </div>

      {ssoEnabledOrgs.length === 0 && (
        <p className={styles.noOrgsMessage}>
          No organizations have SSO enabled. Enable SSO from the Know Admin org edit modal.
        </p>
      )}

      {selectedOrgId && ssoEnabledOrgs.length > 0 && (
        <div className={styles.configPanel}>
          <SsoConfigSection key={selectedOrgId} orgId={selectedOrgId} />
        </div>
      )}

      {ssoEnabledOrgs.length > 0 && (
        <div className={styles.overviewSection}>
          <h2>SSO Overview</h2>
          <table>
            <thead>
              <tr>
                <th>Org Name</th>
                <th>Protocol</th>
                <th>Status</th>
                <th>Last Tested</th>
                <th>Auth Mode</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>{overviewRows}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}
